// Maps a post/idea's queueStatus (or legacy status field) to one of the
// 6 (posts) or 4 (ideas) Kanban column keys. The previous filter in
// KanbanCategory just did `mileVals.includes(project.status || project.queueStatus)`
// which silently dropped any item whose status wasn't already one of the
// hardcoded column ids — i.e., anything in `pending`, `scheduled`,
// `generating-image`, `pending-content-approval`, `partially-posted`,
// etc. didn't appear on the board at all.
//
// This bucket lookup makes every known queueStatus map to SOME column
// so cards stay visible across the whole lifecycle, even if the column
// set is coarser than the underlying enum.
//
// Sources of truth:
//   - Post.queueStatus enum at back/models/post.js#50
//   - Idea.queueStatus enum at back/models/idea.js#49
//   - POST_KANBAN_STATUSES / IDEA_KANBAN_STATUSES at
//     frontend/src/constants/kanbanStatuses.js

const POST_BUCKETS = {
  draft:                      'awaiting-approval',
  pending:                    'awaiting-approval',
  'generating-image':         'awaiting-approval',
  'pending-content-approval': 'awaiting-approval',
  'awaiting-approval':        'awaiting-approval',
  scheduled:                  'awaiting-approval',
  approved:                   'approved',
  posting:                    'posting',
  posted:                     'posted',
  'partially-posted':         'posted',
  rejected:                   'rejected',
  'posting-failed':           'posting-failed',
  'image-generation-failed':  'posting-failed',
  'email-failed':             'posting-failed',
  // Hidden Archived shelf (revealed via "Show archived"). Mirrors the
  // POST_ARCHIVED_STATUS column so an archived post buckets into it.
  archived:                   'archived',
};

const IDEA_BUCKETS = {
  pending:             'pending',
  generating:          'pending',
  'pending-approval':  'pending',
  'awaiting-approval': 'pending',
  failed:              'pending',
  'email-failed':      'pending',
  approved:            'approved',
  rejected:            'rejected',
  posted:              'posted',
};

// Pulls the status-ish string off whichever field it lives on.
function rawStatusOf(item) {
  if (!item || typeof item !== 'object') return null;
  if (typeof item.queueStatus === 'string' && item.queueStatus) return item.queueStatus;
  if (typeof item.status === 'string' && item.status) return item.status;
  // Some legacy records nest a populated Status object under .status.
  if (item.status && typeof item.status === 'object') {
    return item.status.key || item.status.name || String(item.status._id || '');
  }
  return null;
}

// Returns the column key the item should render in, or null when none
// matches (the caller can decide to drop or bucket-into-the-first-column).
function bucketForItem(item, source = 'post') {
  const raw = rawStatusOf(item);
  if (!raw) return null;
  const table = source === 'idea' ? IDEA_BUCKETS : POST_BUCKETS;
  if (table[raw]) return table[raw];
  // If the raw value is ALREADY a known column key (because the kanban
  // just wrote it after a drag), accept it as-is.
  if (Object.values(table).includes(raw)) return raw;
  return null;
}

module.exports = { bucketForItem, rawStatusOf, POST_BUCKETS, IDEA_BUCKETS };
